import { Button, FormField, Input, Select, Text, useToast } from '@ui';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FormDialog } from '../../../app/FormDialog';
import { errorMessage } from '../../../lib/errors';
import {
  useForageSuggestions,
  usePlants,
  useRetrySuggestions,
  useSaveForage,
  type ForageCalendar,
  type Plant,
} from '../api';
import { dayMonth, parseDayMonth, period } from '../format';
import { SuggestionGroup } from './ForageSuggestions';
import styles from './forage.module.css';

interface Row {
  plant: Plant;
  start: string;
  end: string;
  note: string;
}

/** Chooses the plants of a location and overrides bloom periods (TRA-002). */
export function ForageDialog({
  locationId,
  calendar,
  onClose,
}: {
  locationId: string;
  calendar: ForageCalendar;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const plants = usePlants();
  const suggestions = useForageSuggestions(locationId);
  const retry = useRetrySuggestions(locationId);
  const save = useSaveForage(locationId);
  const [rows, setRows] = useState<Row[]>(() =>
    calendar.entries.map((e) => ({
      plant: e.plant,
      start: e.overridden ? dayMonth(e.bloom_start) : '',
      end: e.overridden ? dayMonth(e.bloom_end) : '',
      note: e.note ?? '',
    })),
  );
  const [adding, setAdding] = useState('');
  const [invalid, setInvalid] = useState<Record<string, boolean>>({});

  const chosen = new Set(rows.map((r) => r.plant.id));
  const available = (plants.data ?? []).filter((p) => !p.archived && !chosen.has(p.id));
  const update = (id: string, patch: Partial<Row>) =>
    setRows((rs) => rs.map((r) => (r.plant.id === id ? { ...r, ...patch } : r)));

  const addPlant = (plant: Plant) =>
    setRows((rs) => [...rs, { plant, start: '', end: '', note: '' }]);

  const add = () => {
    const plant = available.find((p) => p.id === adding);
    if (!plant) return;
    addPlant(plant);
    setAdding('');
  };

  const submit = () => {
    const errors: Record<string, boolean> = {};
    const entries = rows.map((r) => {
      const start = parseDayMonth(r.start);
      const end = parseDayMonth(r.end);
      // Both or none: one date alone keeps the catalog's period.
      if (start === null || end === null || (start === undefined) !== (end === undefined)) {
        errors[r.plant.id] = true;
      }
      return {
        plant_id: r.plant.id,
        bloom_start: start ?? null,
        bloom_end: end ?? null,
        note: r.note.trim() || null,
      };
    });
    setInvalid(errors);
    if (Object.keys(errors).length > 0) return;
    save.mutate(
      { entries, version: calendar.version },
      {
        onSuccess: () => {
          toast.show(t('common.saved'));
          onClose();
        },
      },
    );
  };

  return (
    <FormDialog
      open
      drawer
      onClose={onClose}
      title={t('forage.dialog.title')}
      submitLabel={t('common.save')}
      pending={save.isPending}
      error={save.error ? errorMessage(save.error) : undefined}
      onSubmit={submit}
    >
      <SuggestionGroup
        suggestions={suggestions.data}
        chosen={chosen}
        onAdd={(s) => addPlant(s.plant)}
        onRetry={() => retry.mutate()}
        retrying={retry.isPending}
      />
      <div className={styles.addRow}>
        <FormField label={t('forage.dialog.add')}>
          {(p) => (
            <Select
              {...p}
              value={adding}
              onChange={(e) => setAdding(e.target.value)}
              options={[
                { value: '', label: t('forage.dialog.choose') },
                ...available.map((pl) => ({
                  value: pl.id,
                  label: `${pl.name} (${period(pl.bloom_start, pl.bloom_end)})`,
                })),
              ]}
            />
          )}
        </FormField>
        <Button variant="secondary" disabled={!adding} onClick={add}>
          {t('forage.dialog.addButton')}
        </Button>
      </div>
      {rows.length === 0 ? (
        <Text tone="muted">{t('forage.dialog.none')}</Text>
      ) : (
        <ul className={styles.entries}>
          {rows.map((r) => (
            <li key={r.plant.id} className={styles.entry}>
              <div className={styles.entryHead}>
                <span>
                  <strong>{r.plant.name}</strong>
                  <Text as="span" size="xs" tone="muted">
                    {' · '}
                    {t('forage.dialog.catalog', {
                      period: period(r.plant.bloom_start, r.plant.bloom_end),
                    })}
                  </Text>
                </span>
                <Button
                  variant="plain"
                  size="sm"
                  aria-label={t('forage.dialog.remove', { name: r.plant.name })}
                  onClick={() => setRows((rs) => rs.filter((x) => x.plant.id !== r.plant.id))}
                >
                  {t('forage.dialog.removeShort')}
                </Button>
              </div>
              <div className={styles.entryFields}>
                <FormField
                  label={t('forage.dialog.start')}
                  error={invalid[r.plant.id] ? t('forage.dialog.invalid') : undefined}
                >
                  {(p) => (
                    <Input
                      {...p}
                      value={r.start}
                      inputMode="decimal"
                      placeholder={dayMonth(r.plant.bloom_start)}
                      onChange={(e) => update(r.plant.id, { start: e.target.value })}
                    />
                  )}
                </FormField>
                <FormField label={t('forage.dialog.end')}>
                  {(p) => (
                    <Input
                      {...p}
                      value={r.end}
                      inputMode="decimal"
                      placeholder={dayMonth(r.plant.bloom_end)}
                      onChange={(e) => update(r.plant.id, { end: e.target.value })}
                    />
                  )}
                </FormField>
                <FormField label={t('forage.dialog.note')}>
                  {(p) => (
                    <Input
                      {...p}
                      value={r.note}
                      maxLength={300}
                      onChange={(e) => update(r.plant.id, { note: e.target.value })}
                    />
                  )}
                </FormField>
              </div>
            </li>
          ))}
        </ul>
      )}
      <Text size="xs" tone="muted">
        {t('forage.dialog.hint')}
      </Text>
    </FormDialog>
  );
}
